import React from 'react';
import Layout from '../../components/Layout';
import { PageProps } from '../../App';
import { Link } from 'react-router-dom';

// Componente da página de Big Data com tipagem correta
const BigDataPage: React.FC<PageProps> = ({ darkMode, toggleDarkMode }) => {
  return (
    <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="bg-white dark:bg-gray-900">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-lupa-orange to-lupa-yellow overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-lupa-purple-dark/70 to-lupa-purple/30 z-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-heading">
                Big Data
              </h1>
              <p className="mt-6 text-xl text-white/90 max-w-3xl mx-auto">
                Transforme grandes volumes de dados em insights estratégicos e vantagem competitiva para o seu negócio.
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto">
              <path fill="#ffffff" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,149.3C960,160,1056,160,1152,138.7C1248,117,1344,75,1392,53.3L1440,32L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-6">O que é Big Data?</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Big Data refere-se a conjuntos de dados extremamente grandes e complexos que não podem ser processados ou analisados usando métodos tradicionais. Esses dados são caracterizados pelos 3 Vs fundamentais: Volume, Velocidade e Variedade.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Na era digital atual, empresas de todos os setores geram e coletam quantidades massivas de dados a cada segundo. O verdadeiro desafio não está apenas em armazenar esses dados, mas em extrair valor real deles para impulsionar decisões de negócio mais inteligentes e estratégicas.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Na Lupa Data, ajudamos empresas a implementar soluções de Big Data que permitem coletar, armazenar, processar e analisar grandes volumes de dados para transformá-los em insights valiosos e acionáveis, criando vantagem competitiva real no mercado.
              </p>
              <div className="mt-8">
                <Link to="/contato" className="btn-primary">
                  Fale com um especialista
                </Link>
              </div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800 p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-lupa-purple dark:text-white mb-4">Os 3 Vs do Big Data</h3>
              <ul className="space-y-6">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-lupa-orange rounded-full flex items-center justify-center mr-4">
                    <span className="text-white text-xl font-bold">V</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-lupa-purple dark:text-white">Volume</h4>
                    <p className="text-gray-700 dark:text-gray-300">
                      Refere-se à enorme quantidade de dados gerados a cada segundo. Estamos falando de terabytes, petabytes e até exabytes de informações provenientes de diversas fontes como redes sociais, dispositivos IoT, transações comerciais e muito mais. O desafio está em armazenar e processar esse volume massivo de forma eficiente.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-lupa-orange rounded-full flex items-center justify-center mr-4">
                    <span className="text-white text-xl font-bold">V</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-lupa-purple dark:text-white">Velocidade</h4>
                    <p className="text-gray-700 dark:text-gray-300">
                      Representa a rapidez com que novos dados são gerados e a necessidade de processá-los em tempo hábil. Em muitos casos, o valor dos dados está diretamente ligado à velocidade com que podem ser analisados e transformados em ações. Sistemas de Big Data modernos permitem análises em tempo real ou quase real para decisões imediatas.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-lupa-orange rounded-full flex items-center justify-center mr-4">
                    <span className="text-white text-xl font-bold">V</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-lupa-purple dark:text-white">Variedade</h4>
                    <p className="text-gray-700 dark:text-gray-300">
                      Engloba os diferentes tipos e formatos de dados que precisam ser processados. Isso inclui dados estruturados (como bancos de dados relacionais), semiestruturados (como XML e JSON) e não estruturados (como textos, imagens, áudios e vídeos). A capacidade de integrar e analisar essa diversidade de formatos é um diferencial competitivo crucial.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Por que o Big Data é importante */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Por que o Big Data é Importante?</h2>
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="w-16 h-16 mx-auto bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Vantagem Competitiva</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Empresas que utilizam Big Data de forma eficiente conseguem antecipar tendências, entender melhor seus clientes e tomar decisões mais rápidas que seus concorrentes.
                  </p>
                </div>
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="w-16 h-16 mx-auto bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Redução de Custos</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Ferramentas de Big Data permitem identificar ineficiências operacionais, otimizar processos e reduzir desperdícios, gerando economias significativas.
                  </p>
                </div>
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="w-16 h-16 mx-auto bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Inovação Acelerada</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    A análise de grandes volumes de dados permite identificar padrões e oportunidades que impulsionam a criação de novos produtos, serviços e modelos de negócio.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Nossa Solução: Lupa Paranoá */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Nossa Solução: Lupa Paranoá</h2>
            <div className="bg-gradient-to-r from-lupa-purple/10 to-lupa-orange/10 dark:from-lupa-purple/20 dark:to-lupa-orange/20 p-8 rounded-xl shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-lupa-purple dark:text-white mb-4">A Plataforma Completa para Big Data</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    O <Link to="/solucoes/lupa-paranoa" className="text-lupa-orange hover:underline">Lupa Paranoá</Link> é nossa solução de Data Lakehouse projetada especificamente para lidar com os desafios do Big Data. Combinando o melhor dos Data Lakes e Data Warehouses, o Paranoá permite que sua empresa:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                      <span className="text-gray-700 dark:text-gray-300"><span className="font-semibold">Armazene qualquer tipo de dado</span> em um único repositório, eliminando silos de informação.</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                      <span className="text-gray-700 dark:text-gray-300"><span className="font-semibold">Processe dados em tempo real</span> para decisões imediatas e ações oportunas.</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                      <span className="text-gray-700 dark:text-gray-300"><span className="font-semibold">Escale facilmente</span> conforme o volume de dados cresce, sem comprometer o desempenho.</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                      <span className="text-gray-700 dark:text-gray-300"><span className="font-semibold">Integre-se com outras soluções</span> como o LIA (nossa IA conversacional) e o Lupa Cast (nossa solução de forecast).</span>
                    </li>
                  </ul>
                  <div className="mt-8">
                    <Link to="/solucoes/lupa-paranoa" className="btn-primary">
                      Conheça o Lupa Paranoá
                    </Link>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-4">Benefícios para o seu Negócio</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-10 h-10 bg-lupa-orange rounded-full flex items-center justify-center mr-4">
                        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-lupa-purple dark:text-white">Implementação mais rápida</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Nossa metodologia ágil e experiência permitem uma implementação mais rápida e eficiente.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-10 h-10 bg-lupa-orange rounded-full flex items-center justify-center mr-4">
                        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-lupa-purple dark:text-white">Economia de custos</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Unificando suas plataformas de dados, você reduz significativamente os custos de infraestrutura e manutenção.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tecnologias */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Tecnologias de Big Data</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Armazenamento</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Soluções escaláveis para armazenar grandes volumes de dados de forma eficiente e acessível.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Hadoop HDFS
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Amazon S3
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Azure Data Lake Storage
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Google Cloud Storage
                  </li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Processamento</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Ferramentas para processar e analisar grandes volumes de dados de forma distribuída e eficiente.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Apache Spark
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Apache Flink
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Apache Kafka
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    MapReduce
                  </li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Análise e Visualização</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Ferramentas para extrair insights valiosos e visualizar dados de forma clara e intuitiva.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Tableau
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Power BI
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Apache Superset
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Looker
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Casos de Uso */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Casos de Uso do Big Data</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Varejo</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Análise de comportamento do consumidor, otimização de estoque, personalização de ofertas e previsão de demanda.
                </p>
                <div className="text-lupa-orange font-medium">
                  <Link to="/solucoes/lupa-cast">Saiba mais sobre o Lupa Cast →</Link>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Indústria</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Manutenção preditiva, otimização de processos produtivos, controle de qualidade e gestão da cadeia de suprimentos.
                </p>
                <div className="text-lupa-orange font-medium">
                  <Link to="/solucoes/lupa-paranoa">Conheça o Lupa Paranoá →</Link>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Finanças</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Detecção de fraudes, análise de risco, segmentação de clientes e personalização de produtos financeiros.
                </p>
                <div className="text-lupa-orange font-medium">
                  <Link to="/solucoes/lia">Descubra o LIA →</Link>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Marketing</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Segmentação de público, personalização de campanhas, análise de sentimento e otimização de canais.
                </p>
                <div className="text-lupa-orange font-medium">
                  <Link to="/tecnologias/ia">Explore nossa IA →</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Benefícios */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Benefícios do Big Data</h2>
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-lupa-purple dark:text-white mb-4">Para o Negócio</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Tomada de decisão baseada em dados</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Decisões mais precisas e fundamentadas em dados reais, reduzindo riscos e aumentando a assertividade.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Identificação de oportunidades</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Descoberta de novos mercados, produtos e serviços através da análise de tendências e comportamentos.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Redução de custos</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Otimização de processos e operações para maior eficiência, eliminando desperdícios e reduzindo gastos desnecessários.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Experiência do cliente aprimorada</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Personalização de produtos, serviços e interações com base em preferências e comportamentos individuais.</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-lupa-purple dark:text-white mb-4">Para a Tecnologia</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Escalabilidade</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Capacidade de crescer e se adaptar a novas fontes de dados e volumes crescentes sem comprometer o desempenho.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Flexibilidade</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Capacidade de lidar com diferentes tipos de dados e cargas de trabalho, adaptando-se às necessidades específicas do negócio.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Performance</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Processamento rápido e eficiente de grandes volumes de dados, permitindo análises em tempo real ou quase real.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-6 w-6 text-lupa-orange mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lupa-purple dark:text-white">Integração</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Capacidade de se integrar com sistemas existentes e novas tecnologias, como IA e machine learning.</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Tecnologias Relacionadas */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Tecnologias Relacionadas</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link to="/tecnologias/data-lakehouse" className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300 mb-2">Data Lakehouse</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Conheça a arquitetura moderna que combina o melhor dos Data Lakes e Data Warehouses para lidar com Big Data.
                </p>
                <div className="text-lupa-orange font-medium flex items-center">
                  Saiba mais
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
              <Link to="/tecnologias/ia" className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300 mb-2">Inteligência Artificial</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Descubra como a IA e o Machine Learning podem potencializar seus dados para gerar insights ainda mais valiosos.
                </p>
                <div className="text-lupa-orange font-medium flex items-center">
                  Saiba mais
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
              <Link to="/solucoes/lupa-paranoa" className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="w-16 h-16 bg-lupa-purple rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300 mb-2">Lupa Paranoá</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Conheça nossa solução completa de Data Lakehouse, projetada para transformar seus dados em valor real para o negócio.
                </p>
                <div className="text-lupa-orange font-medium flex items-center">
                  Saiba mais
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Perguntas Frequentes</h2>
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Minha empresa precisa de Big Data?</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Se sua empresa gera ou coleta grandes volumes de dados, enfrenta desafios para processá-los ou busca extrair mais valor das informações disponíveis, provavelmente se beneficiaria de soluções de Big Data. O tamanho da empresa não é o fator determinante, mas sim o volume, a velocidade e a variedade dos dados com os quais você trabalha.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Quanto custa implementar uma solução de Big Data?</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    O custo varia conforme as necessidades específicas da sua empresa, o volume de dados e a complexidade da solução. Com o Lupa Paranoá, oferecemos opções escaláveis que se adaptam ao seu orçamento, permitindo que você comece com uma implementação menor e cresça conforme necessário. Entre em contato para uma avaliação personalizada.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Quanto tempo leva para implementar uma solução de Big Data?</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    O tempo de implementação varia conforme a complexidade do seu ambiente de dados, mas com o Lupa Paranoá, podemos entregar os primeiros resultados em 4 a 8 semanas. Trabalhamos com uma abordagem ágil, entregando valor incrementalmente e permitindo que você comece a obter benefícios rapidamente.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Preciso de uma equipe especializada para trabalhar com Big Data?</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Nossas soluções são projetadas para serem acessíveis a usuários de negócio, não apenas a especialistas técnicos. Além disso, oferecemos serviços de consultoria e suporte contínuo para ajudar sua equipe a extrair o máximo valor dos seus dados, independentemente do nível de conhecimento técnico.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-20 bg-gradient-to-r from-lupa-purple to-lupa-purple-dark rounded-xl p-8 md:p-12 shadow-xl">
            <div className="md:flex md:items-center md:justify-between">
              <div className="md:w-2/3">
                <h2 className="text-3xl font-bold text-white mb-4">Pronto para transformar seus dados em resultados?</h2>
                <p className="text-white/90 text-lg mb-6 md:mb-0">
                  Descubra como a Lupa Data pode ajudar sua empresa a implementar soluções de Big Data que geram valor real para o negócio.
                </p>
              </div>
              <div className="md:w-1/3 text-center">
                <Link to="/contato" className="inline-block bg-white text-lupa-purple font-bold py-3 px-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-300">
                  Agende uma demonstração
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BigDataPage;
